/*
 * Copyright (c) 2024. Devtron Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { lazy, Suspense, useEffect, useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import {
    API_STATUS_CODES,
    APPROVAL_MODAL_TYPE,
    BASE_ROUTES,
    BreadcrumbStore,
    DevtronProgressing,
    ErrorScreenManager,
    getUrlWithSearchParams,
    ROUTER_URLS,
    setGlobalAPITimeout,
    showError,
    useUserEmail,
} from '@devtron-labs/devtron-fe-common-lib'

import { useVersionUpdateReload } from '@Components/common/hooks/useVersionUpdate'
import { VersionUpdateProps } from '@Components/common/hooks/useVersionUpdate/types'
import ActivateLicense from '@Pages/License/ActivateLicense'

import { ErrorBoundary, getApprovalModalTypeFromURL, importComponentFromFELibrary } from './components/common'
import { validateToken } from './services/service'

import './css/application.scss'

const NavigationRoutes = lazy(() => import('./components/common/navigation/NavigationRoutes'))
const Login = lazy(() => import('./components/login/Login'))
const GenericDirectApprovalModal = importComponentFromFELibrary('GenericDirectApprovalModal')

const App = () => {
    const [errorPage, setErrorPage] = useState<boolean>(false)
    const [validating, setValidating] = useState(true)
    const [approvalToken, setApprovalToken] = useState<string>('')
    const [approvalType, setApprovalType] = useState<APPROVAL_MODAL_TYPE>(APPROVAL_MODAL_TYPE.CONFIG)

    const { setEmail } = useUserEmail()

    const location = useLocation()
    const navigate = useNavigate()

    const isDirectApprovalNotification =
        location.pathname &&
        location.pathname.includes('approve') &&
        location.search &&
        location.search.includes(`?token=${approvalToken}`)
    const customThemeClassName = location.pathname.startsWith(ROUTER_URLS.NETWORK_STATUS_INTERFACE.ROOT)
        ? 'custom-theme-override'
        : ''

    const defaultRedirection = (): void => {
        if (location.search) {
            const continueUrl = new URLSearchParams(location.search).get('continue')
            if (continueUrl) {
                navigate(continueUrl)
            }
        }
    }

    const toastEligibleRoutes: VersionUpdateProps['toastEligibleRoutes'] = [
        {
            path: `${approvalType?.toLowerCase()}/approve`,
            condition: isDirectApprovalNotification && GenericDirectApprovalModal,
            component: <GenericDirectApprovalModal approvalType={approvalType} approvalToken={approvalToken} />,
            eligibleLocation: `/${approvalType?.toLowerCase()}/approve`,
        },
        {
            path: BASE_ROUTES.LICENSE_AUTH,
            condition: true,
            component: <ActivateLicense />,
            eligibleLocation: ROUTER_URLS.LICENSE_AUTH,
        },
        {
            path: `${BASE_ROUTES.LOGIN.ROOT}/*`,
            condition: !window._env_.K8S_CLIENT,
            component: <Login />,
            eligibleLocation: ROUTER_URLS.LOGIN.SSO || ROUTER_URLS.LOGIN.ADMIN,
        },
    ]

    const reloadVersionConfig = useVersionUpdateReload({
        toastEligibleRoutes,
    })

    const redirectToDirectApprovalNotification = (): void => {
        setValidating(false)
        setApprovalType(getApprovalModalTypeFromURL(location.pathname))

        const queryString = new URLSearchParams(location.search)
        const token = queryString.get('token')
        if (token) {
            setApprovalToken(token)
        }
    }

    async function validation() {
        try {
            const {
                result: { emailId: email },
            } = await validateToken()
            setEmail(email)
            defaultRedirection()
        } catch (err: any) {
            // push to login without breaking search
            if (err?.code === API_STATUS_CODES.UNAUTHORIZED) {
                const loginPath = ROUTER_URLS.LOGIN.SSO
                if (location.pathname.includes(loginPath)) {
                    navigate(`${loginPath}${location.search}`)
                } else {
                    navigate(getUrlWithSearchParams(loginPath, { continue: `${location.pathname}${location.search}` }))
                }
            } else {
                setErrorPage(true)
                showError(err)
            }
        } finally {
            setValidating(false)
        }
    }

    useEffect(() => {
        // If not K8S_CLIENT then validateToken otherwise directly redirect
        //  No need to validate token if on license auth page
        if (!window._env_.K8S_CLIENT && location.pathname !== ROUTER_URLS.LICENSE_AUTH) {
            // By Passing validations for direct email approval notifications
            if (isDirectApprovalNotification) {
                redirectToDirectApprovalNotification()
            } else {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                validation()
            }
        } else {
            setValidating(false)
            defaultRedirection()
        }

        setGlobalAPITimeout(window._env_.GLOBAL_API_TIMEOUT)
    }, [])

    const renderRoutesWithErrorBoundary = () =>
        errorPage ? (
            <div className="full-height-width bg__tertiary">
                <ErrorScreenManager />
            </div>
        ) : (
            <ErrorBoundary>
                <BreadcrumbStore>
                    <Routes>
                        {toastEligibleRoutes
                            .filter(({ condition }) => condition)
                            .map(({ path, component }) => (
                                <Route key={path} path={path} element={component} />
                            ))}
                        <Route path="/*" element={<NavigationRoutes reloadVersionConfig={reloadVersionConfig} />} />
                    </Routes>
                </BreadcrumbStore>
            </ErrorBoundary>
        )

    return (
        <div className={customThemeClassName}>
            <Suspense fallback={null}>
                {validating ? (
                    <DevtronProgressing parentClasses="full-height-width flex bg__primary" classes="icon-dim-80" />
                ) : (
                    renderRoutesWithErrorBoundary()
                )}
            </Suspense>
        </div>
    )
}

export default App
